/**
 * Signup role-selection card. One of two on screen 01 (IFA / Individual).
 *
 * Behaviour: clicking "Continue with Google" kicks off Firebase auth
 * with role pre-selected — the caller passes onPick(role) which sets
 * pendingRole in app state, then the auth handler reads it and writes
 * the role into the user profile doc on first sign-in.
 *
 * Both roles ship an amber "Approval required" pill — copy differs by
 * qualifier (verify SEBI vs review account) per the locked behavioural
 * change in the README ("no timeline promises anywhere").
 */
import { IconBriefcase, IconChart, IconCheck, IconClock, IconGoogle } from "./icons";
import { ThumbIFA, ThumbInvestor } from "./ThumbPreview";
import type { UserRole } from "@/types";

interface RoleCardProps {
  kind: UserRole;
  onPick?: (role: UserRole) => void;
}

export function RoleCard({ kind, onPick }: RoleCardProps) {
  const isAdvisor = kind !== "individual";
  const Icon = isAdvisor ? IconBriefcase : IconChart;
  const Thumb = isAdvisor ? ThumbIFA : ThumbInvestor;
  const copy = {
    ra: {
      title: "I am a Research Analyst",
      sub: "Equity and fund research with the full AI analysis workspace.",
      features: ["Multi-agent equity research and report history", "Fund research, portfolio analytics and monitoring", "Briefing credits and AI credit top-ups"],
      approval: "we review your SEBI research/adviser registration before activation",
    },
    individual: {
      title: "I am an Individual Investor",
      sub: "Tracking my own portfolio and forming my own views.",
      features: [
        "Personal portfolio dashboard",
        "Fund & stock analytics (descriptive)",
        "Watchlist & price alerts",
      ],
      // NO approval gate for retail. RETAIL_FREE_TIER made individual signups
      // instant (App.tsx routes them straight to homeFor(role) regardless of
      // `status`), but this line still rendered an amber "Approval required —
      // we review your account" box as the LAST thing a retail user read before
      // the Google popup. The landing page two clicks earlier promises "Instant
      // access · no card". Advisers genuinely are reviewed; retail is not.
      approval: "",
    },
    mfd: {
      title: "I am a Mutual Fund Distributor",
      sub: "Maintaining factual portfolio records for my clients.",
      features: [
        "Separate client portfolio records",
        "Statement previews and reconciliation",
        "Dated ledgers, exports and recovery history",
      ],
      approval: "we review professional accounts before activating client records",
    },
    ifa: {
      title: "I am an IFA / RIA",
      sub: "Maintaining factual portfolio records for my clients.",
      features: [
        "Multi-client records workspace",
        "Statement previews and reconciliation",
        "Dated ledgers, exports and recovery history",
      ],
      approval: "we review professional accounts before activating client records",
    },
  }[kind];
  const title = copy.title;
  const sub = copy.sub;
  const features = copy.features;
  const approvalCopy = copy.approval;

  return (
    <article
      style={{
        background: "var(--paper)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        position: "relative",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ padding: 10, background: "var(--paper-3)", borderRadius: 10 }}>
        <Thumb />
      </div>

      <div className="row gap-3 center" style={{ padding: "0 6px" }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: isAdvisor ? "#f0ede2" : "var(--brand-soft)",
            color: isAdvisor ? "#4a4519" : "var(--brand-deep)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {title}
          </h3>
          <p
            className="muted"
            style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}
          >
            {sub}
          </p>
        </div>
      </div>

      <ul
        style={{
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 7,
          padding: "0 6px",
        }}
      >
        {features.map((f) => (
          <li
            key={f}
            style={{
              display: "flex",
              gap: 9,
              alignItems: "flex-start",
              fontSize: 13,
              color: "var(--ink-mid)",
              lineHeight: 1.45,
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: isAdvisor ? "#f0ede2" : "var(--brand-soft)",
                color: isAdvisor ? "#4a4519" : "var(--brand-deep)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <IconCheck />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* Only where approval is REAL. Rendered unconditionally, an empty copy
          string left a bare "Approval required ·" — worse than the wrong
          warning it replaced. */}
      {approvalCopy ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 12px",
            borderRadius: 6,
            background: "var(--warn-bg)",
            color: "var(--warn-text)",
            fontSize: 12,
            lineHeight: 1.4,
            margin: "0 6px",
          }}
        >
          <IconClock />
          <span>
            <strong style={{ fontWeight: 600 }}>Approval required ·</strong>{" "}
            {approvalCopy}
          </span>
        </div>
      ) : null}

      <button
        className="btn btn-google btn-lg btn-block"
        style={{ margin: "0 6px" }}
        onClick={() => onPick?.(kind)}
      >
        <IconGoogle />
        <span>Continue with Google</span>
      </button>
    </article>
  );
}
